import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2, Users, Briefcase, ArrowRight, MapPin,
  Sparkles, UserPlus, Search, Handshake, Play,
} from 'lucide-react';
import { format } from 'date-fns';

/* ── Types ── */
interface FeaturedOpp {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  spa_linked: boolean;
  company: { name: string };
}

interface UpcomingEvent {
  id: string;
  title: string;
  date: string;
  city: string;
  country: string;
  focus_tags: string[] | null;
}

/* ── Component ── */
const Index = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [featuredOpps, setFeaturedOpps] = useState<FeaturedOpp[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [oppsLoading, setOppsLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('opportunities')
      .select('id, title, type, sector, country, city, spa_linked, company:companies(name)')
      .eq('spa_linked', true)
      .eq('status', 'open')
      .order('published_at', { ascending: false })
      .limit(6)
      .then(({ data }) => { setFeaturedOpps((data as unknown as FeaturedOpp[]) || []); setOppsLoading(false); });

    supabase
      .from('events')
      .select('id, title, date, city, country, focus_tags')
      .eq('status', 'upcoming')
      .order('date', { ascending: true })
      .limit(3)
      .then(({ data }) => { setUpcomingEvents((data as unknown as UpcomingEvent[]) || []); setEventsLoading(false); });
  }, []);

  /* ── Data ── */
  const valueProps = [
    { icon: Building2, title: t('landing.valueProps.companies.title'), description: t('landing.valueProps.companies.description'), link: '/for-companies' },
    { icon: Briefcase, title: t('landing.valueProps.businesses.title'), description: t('landing.valueProps.businesses.description'), link: '/for-businesses' },
    { icon: Users, title: t('landing.valueProps.professionals.title'), description: t('landing.valueProps.professionals.description'), link: '/for-professionals' },
  ];

  const trustedBy = ['U.S. Department of State', 'DRC Ministry of Trade', 'World Bank', 'African Development Bank', 'USAID'];

  const steps = [
    { num: '01', icon: UserPlus, title: 'Create Your Account', description: 'Sign up and choose your role — Investor, Local Business, or Diaspora Professional.' },
    { num: '02', icon: Search, title: 'Browse Opportunities', description: 'Explore SPA-linked tenders, jobs, joint ventures, and training programs matched to your profile.' },
    { num: '03', icon: Handshake, title: 'Connect & Apply', description: 'Submit applications, track progress in real-time, and build lasting partnerships.' },
  ];

  return (
    <Layout>
      {/* ════════════ HERO ════════════ */}
      <section className="relative overflow-hidden bg-background">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        <div className="container relative py-24 lg:py-36">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            {/* Left – Copy */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge variant="secondary" className="mb-5 gap-1.5 text-xs">
                <Sparkles className="h-3 w-3" />
                Strategic Partnership Agreement
              </Badge>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl leading-[1.1]">
                One Portal,{' '}
                <span className="text-gradient">Multiple Roles</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                {t('landing.hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="gap-2">
                  <Link to={user ? '/opportunities' : '/auth?mode=signup'}>
                    {user ? 'Browse Opportunities' : 'Get Started'}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="gap-2">
                  <Link to="/about-spa">
                    <Play className="h-4 w-4" />
                    Learn About SPA
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Right – Floating Preview Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-6 bg-gradient-to-br from-primary/15 to-accent/20 rounded-full blur-3xl" />
                <div className="relative bg-card border rounded-2xl shadow-2xl p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">Atlas Mining Corp</p>
                      <p className="text-xs text-muted-foreground">Kinshasa, DR Congo</p>
                    </div>
                    <SpaBadge size="sm" className="ml-auto" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground">Senior Geologist — Katanga Belt</h3>
                    <div className="flex flex-wrap gap-1.5">
                      <TypeBadge type="job" />
                      <SectorBadge sector="mining" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Lubumbashi</span>
                    <span>Posted 2 days ago</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════ PARTNER LOGOS ════════════ */}
      <section className="border-y bg-muted/30 py-10">
        <div className="container">
          <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground mb-6">
            Built in partnership with leading institutions
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 lg:gap-x-16">
            {trustedBy.map((org) => (
              <span key={org} className="text-sm font-semibold text-foreground/60 whitespace-nowrap">
                {org}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ HOW IT WORKS ════════════ */}
      <section className="container py-24 lg:py-28">
        <div className="text-center mb-14">
          <Badge variant="secondary" className="mb-4">How It Works</Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Start in <span className="text-gradient">Three Steps</span>
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Whether you're a U.S. investor, a Congolese enterprise, or a diaspora professional — getting started is simple.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.12 }}
            >
              <Card className={`h-full border-2 transition-all duration-200 hover:shadow-lg ${index === 0 ? 'border-primary/30 bg-primary/[0.02]' : 'border-transparent hover:border-primary/10'}`}>
                <CardContent className="p-6">
                  <span className="text-3xl font-bold text-primary/20">{step.num}</span>
                  <div className="mt-4 mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ════════════ FEATURED OPPORTUNITIES ════════════ */}
      {oppsLoading ? (
        <section className="container py-20">
          <h2 className="text-2xl font-bold mb-8">{t('landing.featuredOpportunities')}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-44 w-full rounded-xl" />)}
          </div>
        </section>
      ) : featuredOpps.length > 0 && (
        <section className="bg-muted/20 py-20">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <div>
                <Badge variant="secondary" className="mb-2">SPA-Linked</Badge>
                <h2 className="text-2xl font-bold text-foreground">{t('landing.featuredOpportunities')}</h2>
              </div>
              <Button variant="ghost" asChild className="gap-1.5">
                <Link to="/opportunities?spa=true">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {featuredOpps.map(o => (
                <Link key={o.id} to={`/opportunities/${o.id}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary/10">
                    <CardContent className="p-5 space-y-3">
                      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5" />{o.company?.name}
                      </p>
                      <h3 className="font-semibold text-foreground line-clamp-2 leading-snug">{o.title}</h3>
                      <div className="flex flex-wrap gap-1.5">
                        <TypeBadge type={o.type} />
                        <SectorBadge sector={o.sector} />
                        {o.spa_linked && <SpaBadge size="sm" />}
                      </div>
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                        <MapPin className="h-3 w-3" />{o.city ? `${o.city}, ` : ''}{o.country}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════════════ VALUE PROPOSITIONS ════════════ */}
      <section className="container py-24 lg:py-28">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">DRC-USA Portal</Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Built for Every{' '}
            <span className="text-gradient">Stakeholder</span>
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Connecting U.S. companies, Congolese businesses, and diaspora professionals through one unified platform.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {valueProps.map((prop, index) => (
            <motion.div
              key={prop.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Link to={prop.link} className="block h-full group">
                <Card className="h-full border-2 border-transparent hover:border-primary/20 transition-all duration-200 hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <prop.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-foreground">{prop.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{prop.description}</p>
                    <div className="mt-4 flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ════════════ STATS BAR ════════════ */}
      <section className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground py-16">
        <div className="container">
          <div className="grid gap-8 text-center md:grid-cols-4">
            {[
              { value: '4', label: t('landing.stats.pillars') },
              { value: '$1B+', label: t('landing.stats.investment') },
              { value: '30+', label: t('landing.stats.sectors') },
              { value: '2025', label: t('landing.stats.signed') },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
              >
                <div className="text-4xl font-bold">{stat.value}</div>
                <div className="mt-1 text-sm text-primary-foreground/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ UPCOMING EVENTS ════════════ */}
      {eventsLoading ? (
        <section className="container py-20">
          <h2 className="text-2xl font-bold mb-8">{t('landing.upcomingEvents')}</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
          </div>
        </section>
      ) : upcomingEvents.length > 0 && (
        <section className="bg-muted/30 py-20">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-foreground">{t('landing.upcomingEvents')}</h2>
              <Button variant="ghost" asChild className="gap-1.5">
                <Link to="/events">View all <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {upcomingEvents.map(ev => {
                const d = new Date(ev.date);
                const tags = (ev.focus_tags as string[]) || [];
                return (
                  <Link key={ev.id} to={`/events/${ev.id}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow bg-card">
                      <CardContent className="flex gap-4 p-5">
                        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-primary text-primary-foreground">
                          <span className="text-xl font-bold leading-none">{format(d, 'd')}</span>
                          <span className="text-xs uppercase">{format(d, 'MMM')}</span>
                        </div>
                        <div className="space-y-1.5 min-w-0">
                          <h3 className="font-semibold text-foreground line-clamp-2">{ev.title}</h3>
                          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />{ev.city}, {ev.country}
                          </p>
                          {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {tags.slice(0, 2).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ════════════ CTA ════════════ */}
      <section className="container py-24 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="rounded-3xl bg-primary p-8 md:p-12 lg:p-16 text-center text-primary-foreground"
        >
          <h2 className="text-3xl font-bold md:text-4xl">{t('landing.cta.readyTitle')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-primary-foreground/80">{t('landing.cta.readySubtitle')}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <Button asChild size="lg" variant="secondary" className="bg-background text-foreground hover:bg-background/90">
                <Link to="/dashboard">{t('nav.dashboard')}</Link>
              </Button>
            ) : (
              <Button asChild size="lg" variant="secondary" className="bg-background text-foreground hover:bg-background/90">
                <Link to="/auth?mode=signup">Get Started Free</Link>
              </Button>
            )}
            <Button asChild size="lg" variant="link" className="text-primary-foreground">
              <Link to="/about-spa">{t('nav.aboutSpa')} →</Link>
            </Button>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
};

export default Index;
